/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * ORTB body-editor adapters (#858) — the round-trip core between the stored
 * {@link OrtbBodyTemplate} (`{ mode, body }`) and the flat **leaf list** the
 * guided view renders. Both the Guided tree and the Raw JSON view are
 * projections of the same template, so switching between them must preserve
 * values exactly — that guarantee lives here and is pure/unit-tested.
 *
 * Leaf classification mirrors how the driver resolves the body at runtime
 * (`OpenRtbDriver.resolveTemplateNode`, #862): a string that is exactly one
 * `{token}` is a **macro**; an object carrying `$expr` is an **expression**;
 * everything else is a **static** literal. Keeping these rules in sync keeps
 * what the editor shows equal to what the driver sends.
 */

import type { MacroExpression } from '../../../types/decorator-expression';
import type { OrtbBodyTemplate } from '../../../types/demand-source';
import type { OrtbFieldRef } from './ortb-body-validation';
import { detectedOriginFor, suggestMapping } from '../../../utils/mapping-suggest';
import { describeRequiredClaim, isAnswered, rowSlotsFromLeaf, type RequiredMark } from './mapping-value-round-trip';
import { originOf, readValueOrigin, revertValueOf, type ValueOrigin } from './parameter-editor.adapters';
import {
  appliesTo,
  conceptMembersFor,
  isRequiredHere,
  needsOperatorValue,
  readStoredImportance,
  seedValueFor,
  type DestinationField,
  type DestinationFieldType,
  type FieldImportance,
} from '@destination-field';
import {
  foldOrtbBodyPattern,
  isOrtbBodyLeafNode,
  ORTB_BODY_PATTERN_AT,
  ORTB_BODY_PATTERN_EACH,
  ORTB_BODY_PATTERN_LAST,
  ORTB_BODY_PATTERN_PATHS,
  ortbBodyLeafDefault,
  type OrtbBodyLeafNode as SharedOrtbBodyLeafNode,
  ortbBodyLeafValueType,
} from '@ortb-body-leaf';
import { ORTB_MERGE_BASE, mergeBaseFieldAt, type OrtbMergeBaseField } from '@ortb-merge-base';
import { normalizeWireRendering, type WireRendering } from '@wire-rendering';
import { isStructuredYield, sentType, type ExpressionYieldLookup } from '@expression-yield';
import {
  describeConflictingConceptValues,
  describeDiscardedLiteral,
  describeDriverBuiltField,
  describePastedConceptMember,
  describeUnmatchedConceptValue,
  sampleLiteralPolicy,
} from '@sample-literal-policy';
import {
  catalogEntryFor,
  conceptMemberForWireValue,
  protocolConceptForWireField,
  wireFieldVersions,
} from '@field-catalog';
import {
  conceptRenderingAt,
  conceptWireName,
  fieldSpellings,
  getCatalogField,
  ortbCatalogFieldAtPath,
  ortbSentTypeAtPath,
  getMissingStandardFields,
  ORTB_FIELD_CATALOG,
  nameForVersion,
  normalizeOrtbVersion,
  ORTB_REPLACE_STARTER_SECTIONS,
  ORTB_STANDARD_SECTIONS,
  type OrtbFieldType,
} from '@ortb-field-catalog';

/**
 * **A mapping is a static or an expression** (`X58`, `#1428`). A `{macro}` is an expression node,
 * `{ type: 'macro', name }`, so there is no macro kind beside the other two.
 */
export type LeafKind = 'static' | 'expression';

/** A `{macro}` as the expression it is stored as (`X58`) — delimiters stripped, any default on the node. */
export function macroExpression(token: string, defaultValue?: string): MacroExpression {
  const name = token
    .trim()
    .replace(/^[[{]\{?\s*/, '')
    .replace(/\s*\}?[\]}]$/, '');
  return {
    type: 'macro',
    name,
    ...(defaultValue !== undefined && defaultValue !== '' ? { default: defaultValue } : {}),
  };
}

/** The macro a leaf's expression is, where it is only a macro — `undefined` for anything else. */
export function leafMacroName(expression: MacroExpression | undefined): string | undefined {
  return expression?.type === 'macro' ? expression.name : undefined;
}
export type ScalarType = 'string' | 'number' | 'boolean';
export type PathSegment = string | number;

export interface MappingLeaf {
  /** Path from the body root, e.g. `['device','w']` or `['imp',0,'video','w']`. */
  path: PathSegment[];
  kind: LeafKind;
  /** `static` — the literal value (string | number | boolean | null | array | {}). */
  staticValue?: unknown;
  /** `expression` — the structured `$expr` descriptor. A `{macro}` is a `macro` node here. */
  expression?: MacroExpression;
  /**
   * **This row's `Type`** (`$valueType`) — what its value is, overriding the expression's own `asType`
   * (`FD11` §5, `4e`). A scalar member coerces the engine's string result; a structured one is a
   * declaration the driver leaves as it stands. (Plain macros keep their native type.)
   */
  asType?: DestinationFieldType;
  /**
   * **Pre-`X46` spelling of {@link MappingLeaf.importance} — read at load, never written** (`X46`,
   * 2026-09-02).
   *
   * On this surface it never was an omission answer: an unresolved body key is omitted either way
   * (#880), so the flag decided nothing on the wire and was purely *this destination will not work
   * without a value here*. That is what made the two claims visibly separate, and it is why the
   * body's omission control now **refuses** *may be absent* with a sentence rather than offering a
   * box that decides nothing.
   *
   * It rode on the node as `$optional: false`; the replacement rides on it as `$importance`, and
   * both are stripped with the rest of the node.
   */
  optional?: boolean;
  /**
   * **How much this destination wants a value here** (`X46`).
   *
   * Seeded from the ORTB field catalogue when a leaf is created ({@link defaultLeafFor}) — the
   * protocol's claim is a seed and not a second opinion at render, which is the 2026-08-27 ruling
   * this keeps: *"mark it required, clear the optional box, keep those states in sync"* (Todd).
   * There is now one state to keep, because the box it could contradict has gone.
   *
   * Absent means nobody stated one, which is **not** `'optional'`.
   */
  importance?: FieldImportance;

  /**
   * **`false` where the operator declined the value the driver builds at this path** (`X33a`,
   * `#1266`) — absent means *accept it*, which is what the driver has always done.
   *
   * Distinct from {@link MappingLeaf.suppress}, and the difference is the condition: suppress
   * strips whatever happens, this strips **only when this leaf's own value did not resolve**.
   */
  acceptSystemDefault?: false;

  /**
   * **Never send this key** — the unconditional condition, on a body leaf (`#1184`).
   *
   * It sits **beside** the mapping rather than replacing it, which was the requirement: *"Removing
   * the data isn't the answer, and author may want to toggle back and forth while testing"* (Todd).
   * Selecting `Suppress` on the Expression tab is still legitimate and still works — that is an
   * authored mapping; this is a switch over one that stays intact.
   *
   * It reaches the driver, unlike `optional`, because it changes what goes on the wire: the key is
   * omitted **and** whatever the driver built at that path is stripped.
   */
  suppress?: boolean;
  /**
   * **Whether this leaf's fixed value is a credential** (`CR2`, `#1371`) — stored as `$secret` on the
   * node, encrypted at rest and never read back. `false` is an operator's untick of a suggestion, held
   * for the visit; only `true` is stored.
   */
  secret?: boolean;
  /**
   * **How a list goes out where this field takes a single value** (`4f`, `#1416`) — the row's *Send
   * structured values as*, stored as `$sendAs` and read by the driver through `renderForWire`, the
   * function this surface previews with. Absent means nothing authored: the driver joins a list with
   * commas, as it always has.
   */
  sendAs?: WireRendering;

  /**
   * How this leaf came to be (R4/R4b). Absent means the operator authored it.
   *
   * Persisted in `OrtbBodyTemplateDto.provenance`, a **side-car keyed by path** —
   * the body is sent verbatim, so an attribute written into the document itself
   * would go on the wire.
   */
  provenance?: ValueOrigin;
}

export interface LeafGroup {
  /** Top-level section key: `device`, `regs`, `ext`, `imp`, … (or `(root)`). */
  section: string;
  leaves: MappingLeaf[];
}

/**
 * A string that is exactly one `{token}` / `{{token}}`.
 *
 * **A legacy read, and deliberately not a writer** (`#1184`, 2026-08-27). It is how a macro leaf was
 * stored before the node form, and it cannot distinguish an authored literal from a macro reference:
 * a static value of exactly `{foo}` was silently reclassified as a macro. Documents written from now
 * on say which they mean, and this branch is what keeps an older one readable — the same role
 * `mapping-value-slots.ts` plays for the parameter row's split.
 */
const SINGLE_TOKEN = /^\{\{?\s*([A-Za-z0-9_]+)\s*\}?\}$/;

/**
 * **A body leaf's node form — the row every other mapping surface has** (`#1184`).
 *
 * Ratified by Todd, 2026-08-27: *"`static` makes sense to just store as the value, everything else
 * should carry the node. A user reading the raw json would recognize that `{"$macro": …}` and
 * `{"$expr": …}` make sense."*
 *
 * The problem it solves is that on every other surface a mapping is a **row** — the mapping plus its
 * properties — while here a mapping **was the value**, so anything *about* the mapping had nowhere
 * to go. Six such properties had four different answers: `$type` was a node key (and therefore
 * expression-only), the default was refused, `optional` and `provenance` went to side-cars, `suppress`
 * had no home at all.
 *
 * **The rule, generalised from Todd's:** the node appears exactly when there is something to say
 * beyond the value. For `macro` and `expression` that is always — a token and an expression *are not*
 * values. For `static` it is only when a property is attached, which is what keeps an ORTB body full
 * of plain literals reading as an ORTB body.
 *
 * **It never reaches the exchange**, and the reason is worth stating because the old refusals got it
 * wrong: the body is a *template*, not a payload. `resolveTemplateNode` replaces a recognised node
 * wholesale, so its keys are gone before the merge. Safety comes from **recognition** — from
 * {@link isLeafNode} — and not from the `$` prefix, which on an unrecognised object would be emitted
 * like any other key.
 */
/**
 * Narrowed locally so `$expr` is a {@link MacroExpression} rather than `unknown` — the shared
 * declaration (`@ortb-body-leaf`) is zero-import by design, and this is the reader that has the
 * expression type.
 */
type LeafNode = SharedOrtbBodyLeafNode & { $expr?: MacroExpression };

/**
 * Whether this object is a leaf rather than a nested structure to descend into.
 *
 * **The key list moved to `@ortb-body-leaf` on 2026-08-28** (`#1198`). It lived here *and* in
 * `openrtb.driver.ts`, and the two already disagreed — this one recognised `$optional` and the
 * driver's did not — while a third reader, the body validation sweep, had no copy at all and
 * therefore graded every mapped node as though it were a literal.
 */
const isLeafNode = (v: unknown): v is LeafNode => isOrtbBodyLeafNode(v);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v) && !isLeafNode(v);
}

/** Classify a terminal body value into its mapping kind. */
function classifyValue(value: unknown): Omit<MappingLeaf, 'path'> {
  if (isLeafNode(value)) {
    const shared = {
      asType: ortbBodyLeafValueType(value),
      ...(value.$suppress ? { suppress: true as const } : {}),
      ...(value.$secret === true ? { secret: true } : {}),
      // Narrowed where it is read, so a malformed stored rendering is absent rather than a guess.
      ...(normalizeWireRendering(value.$sendAs) ? { sendAs: normalizeWireRendering(value.$sendAs) } : {}),
      // **The `X46` legacy read, at the store boundary.** `$optional: false` was the only way a
      // pre-split document could say *required*; nothing writes it now.
      ...(readStoredImportance({ importance: value.$importance, optional: value.$optional })
        ? { importance: readStoredImportance({ importance: value.$importance, optional: value.$optional }) }
        : {}),
      // **`false` only** (`X33a`, `#1266`) — absence is *accept the system default*, which is what
      // the driver has always done, so there is no stored `true` and nothing to read for one.
      ...(value.$acceptSystemDefault === false ? { acceptSystemDefault: false as const } : {}),
    };
    if (value.$expr) return { kind: 'expression', expression: value.$expr, ...shared };
    // The body store's legacy read (`X58`): a `$macro` leaf is its macro node, with its `$default` on it.
    if (value.$macro !== undefined) {
      return { kind: 'expression', expression: macroExpression(value.$macro, ortbBodyLeafDefault(value)), ...shared };
    }
    return { kind: 'static', staticValue: value.$static, ...shared };
  }
  if (typeof value === 'string') {
    // Legacy read only — see SINGLE_TOKEN.
    const m = value.match(SINGLE_TOKEN);
    if (m) return { kind: 'expression', expression: macroExpression(m[1]) };
  }
  return { kind: 'static', staticValue: value };
}

/**
 * Flatten a body JSON object into an ordered list of leaves. Objects and
 * arrays-of-values are descended; `$expr` descriptors and scalars are leaves.
 * Empty containers are preserved as static leaves so the round-trip is total.
 */
export function bodyToLeaves(body: Record<string, unknown>): MappingLeaf[] {
  const out: MappingLeaf[] = [];

  const walk = (node: unknown, path: PathSegment[]): void => {
    if (Array.isArray(node)) {
      if (node.length === 0) {
        out.push({ path, kind: 'static', staticValue: [] });
        return;
      }
      node.forEach((item, i) => walk(item, [...path, i]));
      return;
    }
    if (isPlainObject(node)) {
      const keys = Object.keys(node);
      if (keys.length === 0) {
        out.push({ path, kind: 'static', staticValue: {} });
        return;
      }
      for (const k of keys) walk(node[k], [...path, k]);
      return;
    }
    // terminal: scalar, null, or $expr node
    out.push({ path, ...classifyValue(node) });
  };

  walk(body, []);
  return out;
}

/** Serialize a single leaf back to its stored body value. */
function leafToValue(leaf: MappingLeaf): unknown {
  // Properties *of* the mapping, shared by every kind — this is the whole point of the node.
  const props: LeafNode = {
    ...(leaf.asType ? { $valueType: leaf.asType } : {}),
    ...(leaf.suppress ? { $suppress: true as const } : {}),
    // A secret literal needs the node to carry the flag, so it is never bare (`#1371`).
    ...(leaf.secret ? { $secret: true as const } : {}),
    ...(leaf.sendAs ? { $sendAs: leaf.sendAs } : {}),
    // Written only when the operator or the catalogue stated one, so a body of plain literals stays
    // a body of plain literals. `$optional` is **not** written back (`X46`): the legacy spelling is
    // read once and replaced, and emitting both would be the two-answers-on-one-row it closes.
    ...(leaf.importance ? { $importance: leaf.importance } : {}),
    // Only the decline is written, for the reason above: accepting is the absence of a value, and a
    // `true` here would change nothing while inviting the next reader to compare against it.
    ...(leaf.acceptSystemDefault === false ? { $acceptSystemDefault: false as const } : {}),
  };

  switch (leaf.kind) {
    case 'expression':
      return { $expr: leaf.expression as MacroExpression, ...props };
    default:
      // **Bare when there is nothing else to say** (Todd, 2026-08-27) — an ORTB body full of plain
      // literals still reads as an ORTB body, and only a static that carries a property is promoted.
      return Object.keys(props).length === 0 ? leaf.staticValue : { $static: leaf.staticValue, ...props };
  }
}

/** Set a value at a path, creating arrays for numeric segments and objects otherwise. */
function setAtPath(root: Record<string, unknown>, path: PathSegment[], value: unknown): void {
  if (path.length === 0) return;
  let cur: any = root;
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i];
    const nextIsIndex = typeof path[i + 1] === 'number';
    if (cur[seg] === undefined || cur[seg] === null) {
      cur[seg] = nextIsIndex ? [] : {};
    }
    cur = cur[seg];
  }
  cur[path[path.length - 1]] = value;
}

/** Rebuild the body JSON from a leaf list. Inverse of {@link bodyToLeaves}. */
export function leavesToBody(leaves: MappingLeaf[]): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const leaf of leaves) {
    setAtPath(root, leaf.path, leafToValue(leaf));
  }
  return root;
}

/**
 * **The display group a top-level field belongs to** (`#1253`, ratified by Todd 2026-09-11).
 *
 * A `BidRequest` has no section for its own envelope — `tmax`, `at`, `cur`, `bcat` sit at the root
 * of the document — and the guided tree grouped by `path[0]`, so each one became **its own section
 * holding one leaf**, with a `＋ Add tmax field` control under it. Todd: *"I don't like the
 * root-level fields showing up as sections with one element… treating it as a section shows the user
 * the '+ add xx' which doesn't make sense."*
 *
 * So they share one synthetic group. **Synthetic is the load-bearing word: it is a label, never a
 * path segment.** `leavesToBody` builds from `leaf.path` and never reads the section, so a body
 * grouped under this name still serialises flat — which is the property Todd asked for, *"if I added
 * a top-level field in the json view, it would show correctly in the guided view"*, and it holds in
 * both directions because there is only one address.
 *
 * **`request` rather than a new word**, because three things already said it and one did not: the
 * field catalog keys these nine fields under `request`, its own comment calls them *top-level
 * BidRequest scalar fields*, and the auto-sent panel already synthesises a `request` group. Only
 * `ORTB_STANDARD_SECTIONS` disagreed, by omission. *One name per concept* — the concept had a name.
 */
export const ORTB_ROOT_SECTION = 'request';

/**
 * **What a section contributes to a leaf's path** — one rule, read by the code that groups leaves
 * and by the code that builds a new one's address.
 *
 * The root group contributes **nothing**, which is what makes it synthetic. Writing this as a
 * condition in two places is the defect `#1269` is about, and here it would be silent in the worst
 * way: a field added under the group would be stored at `request.tmax`, which is a body key no
 * exchange reads.
 */
export function sectionPathPrefix(
  section: string,
  leaves: MappingLeaf[] = [],
  scope: PatternScope = { kind: 'each' },
): PathSegment[] {
  if (section === ORTB_ROOT_SECTION) return [];
  const idx = sectionArrayIndex(section, leaves);
  if (idx !== undefined) return [section, idx];
  // **An `imp` field is authored on the pattern** (RS4c): every impression unless a scope says one.
  if (ORTB_PATTERN_SECTIONS.has(section)) return [section, ...patternScopeSegments(scope)];
  return [section];
}

/**
 * **Which display group this leaf belongs to** — the read side of {@link sectionPathPrefix}, and the
 * only place the question is answered (`#1253`).
 *
 * A one-segment path **is** a root field; a zero-segment one is the body itself, which the walker
 * emits for an entirely empty document. Both belong to the envelope.
 *
 * It is a named function rather than an expression because it had **three** readers the moment the
 * root group existed — the grouping below, the editor's *which fields are already present*, and its
 * *delete this section*. The third is the one that shows why: `String(leaf.path[0]) !== section`
 * keeps every root leaf when the section is `request`, so deleting the envelope would have deleted
 * nothing, silently. *When a rule is written as a condition in more than one place, give it a name
 * and let both call it.*
 */
export function sectionOfLeaf(leaf: MappingLeaf): string {
  const head = String(leaf.path[0] ?? '');
  // A **declared** section owns its subtree, however deep: `imp[0].video.w` is `imp`'s.
  if (head && head !== ORTB_ROOT_SECTION && ORTB_STANDARD_SECTIONS.includes(head)) return head;
  /**
   * **Otherwise it is the envelope, unless it holds named fields of its own.**
   *
   * The first cut read *one segment* and a test caught it on `bcat: ['IAB25']` — an array, so its
   * leaf is `['bcat', 0]`, two segments, and it stayed its own section. `bcat` is one of the five
   * this change exists for. *A rule written from the case you have is a rule that fits that case.*
   *
   * So the question is not how long the path is, it is **whether the key holds a value or a set of
   * named fields** — which is Todd's complaint stated exactly: a section with one element is silly,
   * a container is not. An index continues a value; a name continues a container.
   */
  const continuesWithAName = leaf.path.length > 1 && typeof leaf.path[1] === 'string';
  return continuesWithAName ? head : ORTB_ROOT_SECTION;
}

/**
 * **A leaf's path inside the group it is shown in** — what a row is labelled with, and what *is this
 * field already present* compares (reported 2026-09-15).
 *
 * Both readers wrote `leaf.path.slice(1)`: *the first segment names the section*. True of every
 * declared section and false of the envelope, where the first segment **is the field** — so a pasted
 * `bcat: ['IAB25', 'IAB26']` rendered rows labelled `[0]` and `[1]`, with nothing saying which array
 * they were in. A root scalar hid it, because slicing its one segment left nothing and the label fell
 * back to the whole path. The envelope contributes no segment ({@link sectionPathPrefix}), so nothing is
 * taken off.
 */
export function pathWithinSection(leaf: MappingLeaf): PathSegment[] {
  return sectionOfLeaf(leaf) === ORTB_ROOT_SECTION ? leaf.path : leaf.path.slice(1);
}

/** A group this large opens shut (`LP14`: *expand a root only when it has fewer than five children*). */
export const TREE_GROUP_OPEN_BELOW = 5;

/**
 * **The groups that open shut on a first visit** (`#1453`, `LP14`) — each with five or more fields,
 * unless one of its fields, or the group itself, is named by a finding. The findings are passed as the
 * refs the body's validator reports, which are leaf labels or a section's name.
 */
export function initiallyCollapsedSections(
  groups: readonly { section: string; leaves: readonly MappingLeaf[] }[],
  findingRefs: readonly string[],
  remembered: RememberedCollapse | null,
): Set<string> {
  const collapsed = new Set<string>();
  for (const group of groups) {
    // A finding opens its group, remembered or not — the page sections' rule (`PanelSection`).
    if (findingRefs.some((ref) => groupHoldsRef(group, ref))) continue;
    // The viewer's last arrangement decides a group it saw; the size rule decides one it did not.
    const shut = remembered?.[group.section] ?? group.leaves.length >= TREE_GROUP_OPEN_BELOW;
    if (shut) collapsed.add(group.section);
  }
  return collapsed;
}

/**
 * **Whether a finding's ref names this group or one of its fields** — the one match for *which group
 * is this finding in*, read by the open rule and by the group header's mark so the two cannot
 * disagree about where a finding is.
 */
export function groupHoldsRef(group: { section: string; leaves: readonly MappingLeaf[] }, ref: string): boolean {
  const key = ref.trim();
  if (key === '') return false;
  return key === group.section || group.leaves.some((leaf) => pathToLabel(leaf.path) === key);
}

/**
 * **A viewer's arrangement of one body's groups: `true` for shut** (`#1453`, Todd 2026-09-28:
 * *"per person for the session"*). Every group drawn is recorded, open ones included, so *the viewer
 * opened this* is not confused with *the viewer never saw it* — a group added since falls to the rule.
 */
export type RememberedCollapse = Readonly<Record<string, boolean>>;

const rememberedCollapseKey = (rememberKey: string) => `bb.ortbBody.collapsed:${rememberKey}`;

/** Read defensively: storage can be missing, blocked, or hold something that is not ours. */
export function readRememberedCollapsed(rememberKey: string | null): RememberedCollapse | null {
  if (!rememberKey) return null;
  try {
    const raw = window.sessionStorage.getItem(rememberedCollapseKey(rememberKey));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return Object.fromEntries(Object.entries(parsed).filter(([, v]) => typeof v === 'boolean')) as RememberedCollapse;
  } catch {
    return null;
  }
}

/** Keep the arrangement of the groups drawn now, beside what was remembered of groups not drawn. */
export function writeRememberedCollapsed(
  rememberKey: string | null,
  sections: readonly string[],
  collapsed: ReadonlySet<string>,
): void {
  if (!rememberKey) return;
  try {
    const next: Record<string, boolean> = { ...(readRememberedCollapsed(rememberKey) ?? {}) };
    for (const section of sections) next[section] = collapsed.has(section);
    window.sessionStorage.setItem(rememberedCollapseKey(rememberKey), JSON.stringify(next));
  } catch {
    // Storage unavailable: the arrangement lasts for the visit.
  }
}

/** Group leaves by their top-level section, preserving first-seen order. */
export function groupLeavesBySection(leaves: MappingLeaf[]): LeafGroup[] {
  const order: string[] = [];
  const map = new Map<string, MappingLeaf[]>();
  for (const leaf of leaves) {
    const section = sectionOfLeaf(leaf);
    if (!map.has(section)) {
      map.set(section, []);
      order.push(section);
    }
    map.get(section)!.push(leaf);
  }
  /**
   * **The envelope leads** (Todd, 2026-09-11: *"is it possible to pin it to the top of the tree"*).
   *
   * First-seen order otherwise puts it wherever the body happens to mention its first root field,
   * which for a pasted request can be halfway down. It is the request's own frame, so it reads first
   * — and it is the one group whose position is not a fact about the document.
   */
  const ordered = order.slice().sort((a, b) => Number(b === ORTB_ROOT_SECTION) - Number(a === ORTB_ROOT_SECTION));
  return ordered.map((section) => ({ section, leaves: map.get(section)! }));
}

/**
 * Narrow the guided tree to leaves matching a filter (#922 · 4).
 *
 * A real bid body carries dozens of leaves across eight sections — the #916 sample has
 * ~45 — so finding `imp.video.plcmt` meant expanding sections and reading down them.
 *
 * Two decisions:
 *
 * - **Matched against the full dotted label**, not the section-relative name. `imp.video`
 *   narrows to one section's video block and `plcmt` finds it wherever it lives, and it
 *   is the same address the catalog, the validator, the resolution mark and the operator's
 *   own mapping rows all use — one path vocabulary on every surface (#1000).
 * - **Array indices are matched away.** The rendered label is `imp[0].video.plcmt`, but
 *   the address an operator carries in their head — and the one the catalog, the spec and
 *   every mapping doc write — is `imp.video.plcmt`. Matching only the literal label means
 *   typing the *documented* path finds nothing, which reads as "this body has no such
 *   field". So both forms are matched; the literal one still works for an operator
 *   copying a label off the row in front of them.
 *
 *   **Revisited when #918 landed (RS4c, 2026-09-18), and it still holds.** The index is now
 *   a real part of the address — `imp[*]` is every impression, `imp[1]` and `imp[last]` one —
 *   and a search for `imp.video.plcmt` still finds all of them, which is the right *search*:
 *   the operator is looking for the field, and each row says which impressions it is for,
 *   in its own label. That is the thing #918 asked the rows to do, done by the bracket rather
 *   than by a provenance chip.
 * - **A section with no match disappears rather than rendering empty.** An empty section
 *   header is a row of noise between the operator and the thing they searched for, and
 *   with eight sections there would be seven of them.
 *
 * An empty query returns the groups untouched (identity, same references), so the
 * unfiltered path costs nothing.
 */
export function filterLeafGroups(groups: LeafGroup[], query: string): LeafGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups;
  const matches = (leaf: MappingLeaf): boolean => {
    const label = pathToLabel(leaf.path).toLowerCase();
    if (label.includes(needle)) return true;
    // `imp[0].video.plcmt`, `imp[*].video.plcmt`, `imp[last].tagid` → `imp.video.plcmt` — the
    // address a catalog and every mapping doc write, whichever impressions the row is scoped to.
    const dotted = label.replace(/\[(\d+|\*|last)\]/g, '');
    if (dotted.includes(needle)) return true;
    // **A concept answers to every spelling** (#1112). The row is stored at one name and
    // rendered under the receiver's, so searching for either has to reach it — otherwise the
    // filter contradicts the row the operator is looking at.
    return fieldSpellings(dotted).some((spelling) => spelling.includes(needle));
  };
  return groups.map((g) => ({ ...g, leaves: g.leaves.filter(matches) })).filter((g) => g.leaves.length > 0);
}

/** Human-readable path label, e.g. `imp[0].video.w`. */
export function pathToLabel(path: PathSegment[]): string {
  let label = '';
  for (let i = 0; i < path.length; i++) {
    const seg = path[i];
    if (typeof seg === 'number') label += `[${seg}]`;
    // **A pattern reads as the addresses it stands for** (RS4c): `$each` is `[*]`, every element;
    // `$at.<key>` is `[<key>]`, the one element — `imp[*].video.w`, `imp[last].tagid`. The same
    // bracket vocabulary a path uses (`genres[0]`, `[last]`), so the row, the finding and the
    // operator's own mapping name one element one way.
    else if (seg === ORTB_BODY_PATTERN_EACH) label += '[*]';
    else if (seg === ORTB_BODY_PATTERN_AT && i + 1 < path.length) label += `[${String(path[++i])}]`;
    else label = label ? `${label}.${seg}` : String(seg);
  }
  return label;
}

/**
 * **A leaf's path with its pattern scope and array indices taken out** — the address a catalog, a
 * merge-base declaration or a concept is keyed by (`imp.video.w`). Six lookups filtered out only the
 * numbers, which was right while `imp[0]` was the only way to address an impression and would read
 * `$each` as a field name the moment the pattern arrived.
 */
export function catalogSegments(path: PathSegment[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < path.length; i++) {
    const seg = path[i];
    if (typeof seg !== 'string') continue;
    if (seg === ORTB_BODY_PATTERN_EACH) continue;
    if (seg === ORTB_BODY_PATTERN_AT) {
      i++;
      continue;
    }
    out.push(seg);
  }
  return out;
}

/**
 * **Which impressions an `imp` leaf applies to** (RS4c) — `each` for `imp.$each.*`, the `$at` key
 * for one element, `null` for anything else (another section, or a literal array in a replace body).
 */
export type PatternScope = { kind: 'each' } | { kind: 'at'; key: string };

export function patternScopeOf(path: PathSegment[]): PatternScope | null {
  if (!ORTB_PATTERN_SECTIONS.has(String(path[0]))) return null;
  if (path[1] === ORTB_BODY_PATTERN_EACH) return { kind: 'each' };
  if (path[1] === ORTB_BODY_PATTERN_AT && path[2] !== undefined) return { kind: 'at', key: String(path[2]) };
  return null;
}

/** The path segments a scope contributes under its section. */
export function patternScopeSegments(scope: PatternScope): PathSegment[] {
  return scope.kind === 'each' ? [ORTB_BODY_PATTERN_EACH] : [ORTB_BODY_PATTERN_AT, scope.key];
}

/**
 * **The scope, in the operator's words** — *every impression*, *impression 2*, *the last impression*.
 * An `$at` index is zero-based like the wire, and a person counts from one, so the words count from
 * one and the row's `imp[1]` keeps the wire's number beside them.
 */
export function describePatternScope(scope: PatternScope): string {
  if (scope.kind === 'each') return 'every impression';
  if (scope.key === ORTB_BODY_PATTERN_LAST) return 'the last impression';
  return `impression ${Number(scope.key) + 1}`;
}

/**
 * The order scopes are shown in: every impression first, then by index, then the last — the same
 * order the driver layers them in (`applyOrtbBodyPattern`), so what is read last is what wins.
 */
export function patternScopeRank(scope: PatternScope | null): number {
  if (!scope || scope.kind === 'each') return -1;
  return scope.key === ORTB_BODY_PATTERN_LAST ? Number.MAX_SAFE_INTEGER : Number(scope.key);
}

// ---------------------------------------------------------------------------
// Template-level adapters
// ---------------------------------------------------------------------------

/**
 * Storage key for a leaf's provenance side-car entry.
 *
 * Deliberately **not** `pathToLabel`: that emits `imp[0].video.mimes` for display,
 * while the resolver (`resolveProvenanceAtPath`) walks dotted segments with numeric
 * indices — `imp.0.video.mimes`. Reusing the display label as a storage key would
 * make every array-scoped lookup miss silently, and a provenance system that
 * silently returns nothing is indistinguishable from one that says "operator-set".
 */
export function pathToProvenanceKey(path: PathSegment[]): string {
  return path.map((seg) => String(seg)).join('.');
}

/**
 * Nearest marked ancestor, falling back from the leaf's own path.
 *
 * Required because the round-trip is **not path-stable for arrays**: `bodyToLeaves`
 * explodes a non-empty array into indexed leaves, so a value authored at
 * `imp.0.video.mimes` returns as `imp.0.video.mimes.0` and an exact-key lookup would
 * miss every array leaf — silently, which reads as operator-set.
 *
 * Walking up is also the honest answer rather than a workaround: the operator
 * authored `mimes` as one thing (the guided view edits scalar arrays inline as a CSV
 * list), so its elements share its provenance. Inheritance only fires where an
 * ancestor path was itself marked, which only happens when that ancestor was authored
 * as a unit — a hand-added sibling under an unmarked parent stays unchipped.
 *
 * **An ancestor lends its mechanism, never its guess** (`#1001`). A detector's confidence and the
 * literal Revert returns to describe the one value they were recorded against; an element inheriting
 * them would offer to revert a list member to the whole list's sample.
 */
function provenanceForPath(sidecar: Record<string, unknown> | undefined, path: PathSegment[]): ValueOrigin | undefined {
  if (!sidecar) return undefined;
  for (let end = path.length; end > 0; end--) {
    const origin = readValueOrigin(sidecar[pathToProvenanceKey(path.slice(0, end))]);
    if (origin) return end === path.length ? origin : originOf(origin.kind);
  }
  return undefined;
}

export function templateToLeaves(template: OrtbBodyTemplate | undefined | null): MappingLeaf[] {
  if (!template?.body || typeof template.body !== 'object') return [];
  // A stored `imp` array not yet folded (a document the repair has not reached) is read as the
  // pattern it meant — the same fold the server applies on save (`foldOrtbBodyPattern`, RS4c), so
  // the editor shows `imp[*]` for what the driver will apply to every impression.
  const folded = foldOrtbBodyPattern(
    template as { mode?: string; body?: Record<string, unknown>; provenance?: Record<string, unknown> },
  ).template as OrtbBodyTemplate;
  const sidecar = (folded as { provenance?: Record<string, unknown> }).provenance;
  return bodyToLeaves(folded.body).map((leaf) => {
    const provenance = provenanceForPath(sidecar, leaf.path);
    return provenance ? { ...leaf, provenance } : leaf;
  });
}

export function leavesToTemplate(leaves: MappingLeaf[], mode: 'merge' | 'replace' = 'merge'): OrtbBodyTemplate {
  // The side-car is built here and never merged into `body`: the body is POSTed
  // verbatim, so a provenance key written into it would reach the exchange.
  const provenance: Record<string, ValueOrigin> = {};
  for (const leaf of leaves) {
    if (leaf.provenance) provenance[pathToProvenanceKey(leaf.path)] = leaf.provenance;
  }

  return {
    mode,
    body: leavesToBody(leaves),
    ...(Object.keys(provenance).length > 0 ? { provenance } : {}),
  };
}

// ---------------------------------------------------------------------------
// Field seeding — shared by the editor's "Add field" / "Add recommended body"
// and by callers that need a starter body without mounting the editor (the
// demand-source wizard seeds one when a source becomes OpenRTB).
// ---------------------------------------------------------------------------

/**
 * **The sections authored as a pattern over the array the driver builds** (RS4c) — `imp`, whose
 * fields go to `imp.$each` (every impression) or `imp.$at.<key>` (one). The shared declaration of
 * which stored arrays fold into a pattern is `ORTB_BODY_PATTERN_PATHS`; this reads it.
 */
export const ORTB_PATTERN_SECTIONS: ReadonlySet<string> = new Set(ORTB_BODY_PATTERN_PATHS);

/**
 * The index a section's fields hang off **where the body holds a literal array there** — a replace
 * body's own `imp` array, which is the operator's whole request and is not a pattern. `undefined`
 * everywhere else, a pattern section included.
 */
export function sectionArrayIndex(section: string, leaves: MappingLeaf[]): number | undefined {
  const existing = leaves.find((l) => String(l.path[0]) === section && typeof l.path[1] === 'number');
  return existing ? (existing.path[1] as number) : undefined;
}

/**
 * Absolute leaf path for a section-relative field path.
 *
 * `scope` says which impressions an `imp` field is for, and is optional **because its absence is the
 * right answer**: every impression is what an `imp` field means unless somebody chose one (RS4c), and
 * every caller that is not the add-field control — a paste, *Add recommended*, a validation fix, the
 * wizard's starter body — means exactly that.
 */
export function buildFieldPath(
  section: string,
  relPath: string,
  leaves: MappingLeaf[] = [],
  scope: PatternScope = { kind: 'each' },
): PathSegment[] {
  const segs = relPath.split('.').map((s) => (/^\d+$/.test(s) ? Number(s) : s));
  // The prefix knows the root group contributes nothing (`#1253`). Restating that here is how
  // `request.tmax` would get written to a body.
  return [...sectionPathPrefix(section, leaves, scope), ...segs];
}

// A newly-added field is pre-mapped so it's useful out of the box, not an empty
// string the operator has to fill in by hand. The field's leaf segment is run
// through the same macro matcher as paste-import — `device.ua` → `{ua}`,
// `regs.ext.us_privacy` → `{us_privacy}`, `imp.video.w` → `{w}`. When nothing
// matches, we seed a type-correct empty literal (array/object stay well-formed
// JSON; scalars start blank) rather than a string placeholder in a numeric slot.
const EMPTY_BY_TYPE: Record<OrtbFieldType, unknown> = {
  string: '',
  number: '',
  boolean: '',
  array: [],
  object: {},
};

/**
 * **What a body leaf sends, and whether *Send structured values as* is offered on it** (`4f`,
 * `#1416`) — one answer, read by the control and by the shape finding's redirect, so the finding can
 * never name the control where the row does not show it (`#1272`).
 *
 * `sends` is `sentType`: the row's `Type`, else the slot's declaration, else the expression's yield.
 * It is what the driver keys `renderForWire` on, so it is what the control's example renders with.
 *
 * **Offered where a list could arrive at a field that takes one value.** The slot must send a single
 * value (or declare nothing), and the value must be derived and not known to be a single value. A
 * plain path has no declared yield until field declarations land (`#1205`, `FD11` §2), so unknown
 * counts: a strict *declared list against declared value* gate would never open for `genres` into
 * `site.page`, the case it exists for. **A stored rendering is always shown**, so a leaf whose field
 * changed can still see and clear what it carries.
 */
export function bodyLeafRendering(
  leaf: MappingLeaf,
  lookup: ExpressionYieldLookup | null,
): { sends: DestinationFieldType | null; offered: boolean } {
  const expression = leaf.kind === 'expression' ? leaf.expression : undefined;
  const sent = sentType(leaf.asType, expression, catalogValueKindForPath(leaf.path) ?? null, lookup);
  const sends = sent?.type ?? null;
  if (leaf.sendAs) return { sends, offered: true };
  if (!expression || expression.type === 'suppress' || expression.type === 'static') return { sends, offered: false };
  const slotTakesOne = sends === null || !isStructuredYield(sends);
  const couldBeAList = sent?.yields === undefined || isStructuredYield(sent.yields);
  return { sends, offered: slotTakesOne && couldBeAList };
}

/**
 * The scalar type OpenRTB declares for a leaf's path, if the catalog knows it.
 *
 * The inverse of {@link buildFieldPath}: numeric segments are array indices, and
 * catalog paths are relative to one element (`imp` fields are keyed `video.mimes`,
 * not `0.video.mimes`), so indices are dropped.
 *
 * This is what lets the static editor default its **Send as** to the type the spec
 * already states. Typing `0` into `imp[0].bidfloor` produced the string `"0"`
 * because `EMPTY_BY_TYPE.number` seeds `''` and a text input yields text — while the
 * same `0` typed into Raw JSON was a number, which is the inconsistency reported.
 * The operator should not have to tell us that `bidfloor` is numeric.
 */
/**
 * **What the catalog declares this leaf takes** — the declaration's own type, all five members
 * (`#1130`, and `4e`). `catalogScalarTypeForPath` answers only the scalar half, and `'array'` could
 * not reach the static editor through it, so a field holding one value had no way to become the list
 * it takes.
 *
 * **It was a console `ValueKind` of `ScalarType | 'list'` until 2026-09-23** — one concept under two
 * words beside `DestinationFieldType`'s `array` (`X3`). Widening `Type` to the declaration's five
 * members was the moment that stopped being free, so the console reads the declaration's vocabulary
 * now and says *list* only where an operator reads it (`EXPRESSION_YIELD_LABEL`).
 */
export function catalogValueKindForPath(path: PathSegment[]): DestinationFieldType | undefined {
  // What a value here is **sent as** (`FD13`), which is the declared type except on a concept-valued
  // field, whose conversion the concept renderer owns. The driver reads the same answer.
  return ortbSentTypeAtPath(path) ?? undefined;
}

/**
 * **What the catalog declares at a leaf's path** — the section split every lookup here was doing
 * for itself.
 *
 * Six call sites held the same three lines (`catalogSegments`, the two-segment guard, the section
 * split), which is the per-caller copy of *where does this address live in the catalog*. It is one
 * answer now, so a leaf's declaration — its type, its concept, its importance — is fetched the same
 * way wherever it is asked for.
 *
 * **A top-level field is in the `request` section, and this answered nothing for one until `4b`.**
 * The guard read *fewer than two segments* as *no section*, while the section that groups `tmax`,
 * `at`, `cur`, `test` and the block lists is `request`, which is not part of the path (`#1194`) —
 * the value sweep's `fieldSegments` has always mapped it that way. So nine fields had no declaration
 * on every reader here: no declared type for the static editor, no concept check (`4d`), and no
 * grade for a default. A reader narrower than the declaration it reads, found grading `tmax`'s
 * default.
 */
export function catalogFieldForPath(path: PathSegment[]): DestinationField | undefined {
  // The driver asks the same question of the same address (`FD13`, `#1430`), so there is one answer.
  return ortbCatalogFieldAtPath(path);
}

export function catalogScalarTypeForPath(path: PathSegment[]): ScalarType | undefined {
  const type = catalogFieldForPath(path)?.valueType;
  return type === 'string' || type === 'number' || type === 'boolean' ? type : undefined;
}

/**
 * The concept members a leaf may take, for the version this source speaks — `[]` where the
 * path is not concept-valued (`N11h`, #1112).
 *
 * Filtered rather than merely listed, because 2.5 has a faithful value for `instream` alone:
 * offering the operator a member the driver will then omit is the #1017 family wearing a
 * dropdown.
 */
export function catalogConceptMembersForPath(path: PathSegment[], protocolVersion?: string): string[] {
  const field = catalogFieldForPath(path);
  return field ? conceptMembersFor(field, { protocolVersion }) : [];
}

/**
 * **What this leaf is called on the wire, for the version this source speaks** — `undefined`
 * where the path names no concept, which is every other leaf in the body.
 *
 * The row is *named* by this (#1112, 2026-08-27). The one-entry catalog made the operator
 * author `video.placement` on a 2.6 source and then read `"plcmt": 1` in the request, with
 * nothing in the console using the second word: correct bytes, and an editor that does not
 * describe them. The stored address does not move — it is the identity everything else keys
 * on, from per-path attribution to inheritance — so this is what the row **shows**, not what
 * it saves.
 */
export function conceptWireLabelForPath(path: PathSegment[], protocolVersion?: string): string | undefined {
  const named = catalogSegments(path);
  if (named.length < 2) return undefined;
  const [, ...rest] = named;
  return conceptWireName(rest.join('.'), protocolVersion);
}

/**
 * The resolution mark for a concept leaf — `instream → plcmt:1`, or the omission where this
 * version cannot express what was authored.
 *
 * **A concept row is the one static leaf whose resolution is not itself** (Todd, 2026-08-27:
 * *"that mirrors the 'what you wrote' → 'what it resolves to' pattern we use for derived
 * fields"*). Everywhere else a literal is sent as written, which is why the editor suppresses
 * the mark on a static row; here the operator writes a meaning and we send a name and an
 * integer, so hiding the second half hides the whole translation.
 *
 * It is **declared, never observed** — read from the catalog rather than from a preview — so
 * it is never `stale`: nothing has to be applied for it to be true.
 */
export function conceptResolutionForLeaf(
  leaf: MappingLeaf,
  protocolVersion?: string,
): { value?: string; unresolved?: boolean } | undefined {
  if (leaf.kind !== 'static') return undefined;
  const named = catalogSegments(leaf.path);
  if (named.length < 2) return undefined;
  const [, ...rest] = named;

  const rendering = conceptRenderingAt(rest.join('.'), leaf.staticValue, protocolVersion);
  if (!rendering) return undefined;
  if (rendering.outcome === 'rendered') return { value: `${rendering.field}:${rendering.value}` };
  if (rendering.outcome === 'not-a-concept') return undefined;
  // `unknown-member` and `unexpressible` are the same *outcome* on the wire — the field is
  // left off — and the row says so with the vocabulary it already has. Which of the two it
  // was belongs in validation, where there is room to say why.
  return { unresolved: true };
}

/**
 * The required mark for a body leaf — the same claim a parameter row makes, read for **this**
 * surface (`N11g`, 2026-08-27).
 *
 * A leaf has carried `optional` since `#1184` and the collapsed row said nothing about it, so the
 * flag was visible only to whoever expanded the row that already shows the checkbox — the mark's
 * own premise (*invisible until the row is expanded*) left standing on the surface it was not
 * built for.
 *
 * ## It reads **one** state, and the protocol seeds that state rather than joining it
 *
 * The first build OR-ed two sources — the catalog's `importance: 'required'` and the leaf's own
 * `optional: false` — and Todd found the crack the same afternoon: `video.mimes` showed
 * **REQUIRED** beside an *Optional* box that was ticked. Two controls on one row, answering what
 * the operator reads as one question.
 *
 * > *"`required` to the user means it has to be on the wire — that's why required + optional felt
 * > so weird… mark it required, clear the optional box, keep those states in sync."* (Todd)
 *
 * So the catalog's claim is a **default at the moment a leaf is created** ({@link defaultLeafFor}),
 * not a second input at render. The badge and the checkbox are then the same bit and cannot
 * contradict, and an operator who ticks *Optional* on a protocol-required field has said something
 * the product must hear — their exchange may genuinely not need it. If that is wrong it is a
 * **validation** finding, which is the surface with room to say why; a badge that silently refused
 * to move would just be arguing with a checkbox.
 *
 * **The consequence differs from the parameter row's, and the tooltip says so.** A required VAST
 * parameter with nothing in it is sent as `foo=`; an ORTB body key that resolves to nothing is
 * **omitted** (#880) whatever this flag says.
 */
export function describeLeafRequiredMark(leaf: MappingLeaf, isTemplate: boolean | null): RequiredMark | null {
  return describeRequiredClaim(
    {
      // **`importance`, not `!optional`** (`X46`) — on this surface the flag never meant anything
      // on the wire, so it was only ever this claim wearing the omission word.
      required: isRequiredHere(leaf.importance) && !leaf.suppress,
      answered: isAnswered(rowSlotsFromLeaf(leaf)),
      whenUnresolved: 'omit',
    },
    isTemplate,
  );
}

/**
 * Does the protocol itself expect a value at this path?
 *
 * Read **once, when a leaf is created** — see {@link describeLeafRequiredMark} for why it is not a
 * second input to the badge.
 */
function catalogRequiresPath(section: string, relPath: string): boolean {
  return getCatalogField(section, relPath)?.importance === 'required';
}

/** The dotted address of a leaf, indices collapsed — the form the merge base declares. */
export function mergeBasePathOf(leaf: MappingLeaf): string {
  // Scope taken out as well as indices (RS4c): the merge base declares `imp.video.w`, not `$each`.
  return catalogSegments(leaf.path).join('.');
}

/** One section's worth of what the driver sends unprompted. */
export interface AutoSentGroup {
  readonly section: string;
  readonly fields: readonly OrtbMergeBaseField[];
  /** How many of them go out on every request, whatever the session carries. */
  readonly alwaysCount: number;
}

/**
 * **What the driver still sends here with nothing authored, by section** (§15, #1117).
 *
 * The VAST rule, on the body: the panel lists what goes out *unprompted*, so authoring a field
 * **removes it from the panel** — that is the whole signal, and it is why there is no separate
 * "you have overridden this" state to keep in step.
 *
 * **Grouped by section, and that is what makes it readable rather than a wall.** A fresh merge
 * body has 79 of these; flat, it is a list nobody opens twice. Grouping also settles a question
 * a flat list could not answer honestly: `site.content.title` and `app.content.title` are the
 * same concept on mutually exclusive halves of the spec, and collapsing them into one row would
 * have to pick a section to put it in (Todd: *"which section would it collapse to?"*). Under
 * their own headings there is nothing to collapse — an operator reading a `site` group and an
 * `app` group already knows only one applies.
 *
 * Empty in **replace** mode, and not as a special case: replace discards the procedural body, so
 * there is nothing being sent unprompted to list. A panel saying otherwise would be the #1045
 * defect inside the panel built to prevent one.
 *
 * Section order follows the body tree's, so the panel and the thing it describes read top to
 * bottom the same way.
 *
 * ## `always` alone by default, the rest behind a checkbox
 *
 * Todd, on what the panel promises: *"what the user expects to see there is **if I did nothing,
 * this is what would get sent**"* — which is the VAST panel's promise, and it is true of the
 * `always` set and **not** of the other two. 22 fields answer that question; 57 more answer a
 * different one (*and these, when the session has them*), and mixing them makes the headline
 * claim false for three quarters of the list.
 *
 * So the default is the answer to the question, and `includeWhenKnown` is the operator asking
 * the second one — his own suggestion, and better than a separate list, because the two sets
 * share a shape, a sort order and a per-row Edit.
 */
export function autoSentGroups(
  leaves: MappingLeaf[],
  mode: OrtbBodyTemplate['mode'],
  includeWhenKnown = false,
): AutoSentGroup[] {
  if (mode !== 'merge') return [];
  const authored = new Set(leaves.map(mergeBasePathOf));

  const order: string[] = [];
  const bySection = new Map<string, OrtbMergeBaseField[]>();
  for (const field of ORTB_MERGE_BASE) {
    if (authored.has(field.path)) continue;
    if (!includeWhenKnown && field.certainty !== 'always') continue;
    // A top-level scalar (`tmax`, `at`, `cur`) has no section of its own; it belongs to the
    // request itself, which is the same name the field catalog gives that group.
    const section = field.path.includes('.') ? field.path.split('.')[0] : 'request';
    if (!bySection.has(section)) {
      bySection.set(section, []);
      order.push(section);
    }
    bySection.get(section)!.push(field);
  }

  return order.map((section) => {
    const fields = bySection.get(section)!;
    return { section, fields, alwaysCount: fields.filter((f) => f.certainty === 'always').length };
  });
}

/**
 * The merge-base field this leaf sits on top of, or `undefined` where the operator is authoring
 * something the driver never builds.
 *
 * *"Fields you add here merge over that base"* has been on the surface as prose since #858; this
 * is the same sentence per row, which is where an operator can act on it — set `video.maxduration`
 * to a constant and it stops following the pod, and today nothing on that row says so.
 */
export function overriddenMergeBaseField(
  leaf: MappingLeaf,
  mode: OrtbBodyTemplate['mode'],
): OrtbMergeBaseField | undefined {
  return mode === 'merge' ? mergeBaseFieldAt(mergeBasePathOf(leaf)) : undefined;
}

/**
 * **What goes out once a parent's body leaf is removed from this source** (`#1379`) — the phrase
 * *Sent automatically* puts in *"…so ___ goes out instead"*.
 *
 * Version-aware because `ORTB_MERGE_BASE` is not: it lists both versions' locations for a privacy
 * signal, and the driver writes the one the receiver reads. Removing a parent's `user.consent` from a
 * 2.5 source puts nothing at `user.consent`; the consent string goes to `user.ext.consent`. Saying
 * *the driver's own `user.consent`* there would be the `#1045` defect in the sentence that explains
 * the act.
 */
export function bodyRemovalReplacement(label: string, protocolVersion: string | null | undefined): string {
  const built = mergeBaseFieldAt(label);
  if (!built) return 'nothing';
  const what = `${built.from.charAt(0).toLowerCase()}${built.from.slice(1)}`;
  const [section, ...rest] = label.split('.');
  const name = rest.join('.');
  const version = normalizeOrtbVersion(protocolVersion ?? undefined);
  const field = (ORTB_FIELD_CATALOG[section] ?? []).find((f) => f.name === name);
  if (field?.when && !appliesTo(field, { protocolVersion: version })) {
    const current = nameForVersion(section, name, version);
    return current
      ? `${what}, at ${section}.${current} where OpenRTB ${version} reads it,`
      : `nothing here (OpenRTB ${version} has no ${label})`;
  }
  return `what the driver builds here (${what})`;
}

/**
 * Does the destination at this path declare that **only the operator has the value**?
 *
 * `from: 'operator'` already carried the rule a paste needs — *take what a pasted tag supplied;
 * otherwise leave it unfilled* — and nothing on the body path was asking (`#1186`).
 */
export function isOperatorSuppliedPath(path: PathSegment[]): boolean {
  const field = catalogFieldForPath(path);
  return !!field && needsOperatorValue(field);
}

/** What a paste decided about one field, and why. */
export interface PastedLeafDecision {
  /** The leaf to import, or `null` where the field was not imported at all. */
  readonly leaf: MappingLeaf | null;
  /** Operator-facing, per field — `null` where nothing needs saying. */
  readonly note: string | null;
}

/**
 * **What a pasted body field becomes** (`#1187`).
 *
 * The rule is Todd's, 2026-08-27, and it is three paths rather than the one the editor had:
 *
 * | | | |
 * | --- | --- | --- |
 * | *rightly ours, built by our own internal processing* | `certainty: 'always'` | **not imported**, and it is in *Sent automatically* |
 * | *ours if we know it* | `certainty: 'when-known'` | **mapped, literal kept as the default** |
 * | *rightly yours* | `from: 'operator'`, or a name no catalog knows | **kept as written** |
 *
 * What the editor did instead was drop everything under `id` and `imp` by section, which took a
 * partner's `tagid`, `protocols` and `minduration` with it and announced only *"skipped
 * driver-managed field(s)"*. A section is not a unit anybody's request is organised by.
 *
 * **One veto sits over all three**: `perRequest` and `consent` (`#1188`). A viewer's IP is *ours
 * if we know it* and its literal must still be discarded — a stored copy is a stranger's address
 * sent to every subsequent viewer, and a stored consent string is a position somebody never took.
 * Those get the mapping with **no** default, and the sentence explaining it is the one the VAST
 * parser has used since `#952`, now shared rather than reimplemented.
 *
 * **The drop only became honest with the panel.** Before `#1117` a discarded
 * `imp.video.maxduration` left the operator's view entirely; now it is listed, described, and one
 * click from being overridden.
 */
export function classifyPastedLeaf(leaf: MappingLeaf, protocolVersion?: string): PastedLeafDecision {
  const label = mergeBasePathOf(leaf);
  // **Scalars only.** A default is a short literal on the wire; an object or an array has no
  // representation as one, and `String({})` is `[object Object]` — a value that looks like a
  // default and is nothing. An unrepresentable literal is simply not offered as one.
  const value = leaf.kind === 'static' ? leaf.staticValue : undefined;
  const literal =
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';

  // 1 · **Theirs, and it outranks ours** — a slot the receiver assigns, or a name nothing of ours
  // claims. `imp.tagid` is why the order matters and not merely the outcome: the driver *does*
  // build it on every request, from the inbound ad unit, so asking the merge base first threw
  // away the exchange's own placement id as *driver-managed*. Both claims are true; the operator's
  // wins, which is the precedence `buildRequestHeaders` and `applyCustomFields` already keep —
  // procedural fills a gap, it does not win an argument.
  // **Every imported field is marked** (`#1001`, R4b). Until then this path marked nothing, so a whole
  // pasted body rendered as though the operator had typed it — including the fields we mapped for
  // them, which is the one reading the chip exists to prevent.
  const asWritten: MappingLeaf = { ...leaf, provenance: originOf('detected') };
  if (isOperatorSuppliedPath(leaf.path)) return { leaf: asWritten, note: null };

  // 1b · **A concept, whichever version spelled it** (reported 2026-09-15). Before the merge-base
  // check, because that check knows the driver builds `plcmt` and nothing else: it dropped a pasted
  // `plcmt: 2` (accompanying) as driver-built — so the driver then sent `instream` — and kept
  // `placement: 1` as a raw number, which on a 2.6 source is sent as the 2.5 field **with no `plcmt`
  // at all**. Both spellings are one concept; the number is read back by the name it arrived under.
  const conceptDecision = classifyPastedConceptLeaf(leaf, label, asWritten);
  if (conceptDecision) return conceptDecision;

  // 2 · Ours, on every request. Not imported — a static copy pins a value the driver recomputes
  // per pod, and `imp.video.maxduration` frozen at a partner's example is the #1045 shape: it
  // looks configured and it silently stops following the break.
  const built = mergeBaseFieldAt(label);
  if (built?.certainty === 'always') {
    return { leaf: null, note: describeDriverBuiltField(label, built.from) };
  }

  const suggestion = suggestMapping(String(leaf.path[leaf.path.length - 1]), leaf.staticValue, 'body');
  // A value shaped like a viewer's under a name nothing recognises is kept as written and **said**
  // (`#1016`) — the sentence is the tag parser's, from the shared policy.
  if (suggestion.kind !== 'macro' || !suggestion.macroToken) {
    return { leaf: asWritten, note: suggestion.perViewerShape ? suggestion.reason : null };
  }

  // 3 · Ours if we know it: map it, and keep what they sent as the default standing behind the
  // mapping — unless the value is not ours to store. The origin records how sure the match was and
  // what Revert returns to, under the same veto (`#1001`).
  const macro = suggestion.macroToken.replace(/[{}]/g, '');
  const outcome = sampleLiteralPolicy(catalogEntryFor(macro));
  const mapped: MappingLeaf = {
    path: leaf.path,
    kind: 'expression',
    expression: macroExpression(macro),
    provenance: detectedOriginFor(suggestion),
  };

  if (outcome !== 'retain') {
    return { leaf: mapped, note: describeDiscardedLiteral(outcome, label, literal, macro) };
  }
  // A concept row is authored in the concept's own vocabulary, so a partner's integer is not a
  // default we can express — `renderConceptForReceiver` would have nothing to do with it.
  if (catalogConceptMembersForPath(leaf.path, protocolVersion).length > 0) return { leaf: asWritten, note: null };

  return { leaf: literal === '' ? mapped : { ...mapped, expression: macroExpression(macro, literal) }, note: null };
}

/**
 * **A pasted concept field, read as the concept** — `undefined` where the leaf is not one, so the
 * caller carries on.
 *
 * | The sample carried | Becomes |
 * | --- | --- |
 * | the member the driver assumes and builds anyway (`placement: 1`, `plcmt: 1`) | **not imported**, noted as driver-built — the same as `plcmt` always was |
 * | any other member (`plcmt: 2`) | the **member**, at the catalog's one address (`video.placement`), marked detected |
 * | a number no member means (`placement: 3`, 2.5's In-Article) | **kept as written**, with a note that it goes out under that name on any version |
 *
 * The member is stored at the concept's address rather than the pasted spelling, because that is the
 * one leaf the driver renders for the receiver's version. A revert target is kept only where the
 * address did not change: reverting `plcmt: 2` onto `video.placement` would write the number under the
 * other list's field.
 */
function classifyPastedConceptLeaf(
  leaf: MappingLeaf,
  label: string,
  asWritten: MappingLeaf,
): PastedLeafDecision | undefined {
  const wireField = leaf.path[leaf.path.length - 1];
  const host = leaf.path[leaf.path.length - 2];
  if (typeof wireField !== 'string') return undefined;
  const concept = protocolConceptForWireField(wireField);
  if (!concept || host !== concept.on || leaf.kind !== 'static') return undefined;

  const value = leaf.staticValue;
  if (typeof value !== 'number') return undefined;

  const versions = wireFieldVersions(concept.concept, wireField);
  const member = conceptMemberForWireValue(concept.concept, wireField, value);
  if (!member) {
    return {
      leaf: asWritten,
      note: describeUnmatchedConceptValue(label, wireField, value, concept.concept, versions),
    };
  }

  // `mergeBaseFieldAt` answers a concept under any spelling, so this asks what every reader asks.
  const built = mergeBaseFieldAt(label);
  if (member === concept.assumed && built?.certainty === 'always') {
    return { leaf: null, note: describeDriverBuiltField(label, built.from) };
  }

  const address = [...leaf.path.slice(0, -1), concept.concept];
  const sameAddress = concept.concept === wireField;
  return {
    leaf: {
      path: address,
      kind: 'static',
      staticValue: member,
      ...(leaf.importance ? { importance: leaf.importance } : {}),
      provenance: { kind: 'detected', confidence: 'high', ...(sameAddress ? { sample: { value } } : {}) },
    },
    note: describePastedConceptMember(label, value, member, concept.concept, versions),
  };
}

/**
 * **What a pasted bid request becomes, as a whole** — the fields to import and what to tell the operator
 * about the rest (reported 2026-09-15).
 *
 * Two things a per-field decision cannot see, and both came from one real request carrying ten
 * identical impressions:
 *
 * - **Identical impressions are imported as one.** The driver repeats an authored `imp` element across
 *   every slot only when the array has exactly one element (`DEMAND_REQUEST_SHAPE_DESIGN.md` RS4a).
 *   Ten copies are ten per-index overrides, and slots past the tenth quietly receive the driver's own
 *   impression — so importing them as written authors something the operator did not mean. Impressions
 *   that differ are per-slot content and stay per index; a defaults model for those is `#918`'s.
 * - **A note is stated once.** It names a field by its index-free path, so a field inside an array — or
 *   inside ten impressions — produced the same sentence once per element: 258 lines for that request,
 *   of which a few dozen were different.
 */
export function classifyPastedBody(
  body: Record<string, unknown>,
  protocolVersion?: string,
): { leaves: MappingLeaf[]; notes: string[] } {
  const { body: collapsedBody, note: collapsed } = collapseIdenticalImpressions(body);
  // Then into the pattern it means (RS4c): one impression is every impression, several are each their
  // own — the same fold the server applies on save, so a paste imports the shape it will be stored in.
  const shaped = foldOrtbBodyPattern({ body: collapsedBody }).template.body;
  const perImpression = perImpressionNote(collapsedBody);
  const decisions = bodyToLeaves(shaped).map((leaf) => classifyPastedLeaf(leaf, protocolVersion));
  const imported = decisions.map((d) => d.leaf).filter((leaf): leaf is MappingLeaf => leaf !== null);
  const fieldNotes = decisions.map((d) => d.note).filter((note): note is string => note !== null);
  const { leaves, conflicts } = oneLeafPerAddress(imported);
  return {
    leaves,
    notes: [
      ...new Set([
        ...(collapsed ? [collapsed] : []),
        ...(perImpression ? [perImpression] : []),
        ...fieldNotes,
        ...conflicts,
      ]),
    ],
  };
}

/**
 * **Impressions that differ are imported one each, and that has a consequence worth saying** (RS4c):
 * an impression past the ones pasted gets none of them, so a field meant for all of them belongs on
 * `imp[*]`.
 */
function perImpressionNote(body: Record<string, unknown>): string | null {
  const imp = body.imp;
  if (!Array.isArray(imp) || imp.length < 2) return null;
  return (
    `The sample's ${imp.length} impressions differ, so each was imported for its own impression (imp[0] to ` +
    `imp[${imp.length - 1}]). An impression past the ${imp.length}th gets none of them; a field every impression ` +
    'should carry belongs on imp[*].'
  );
}

/**
 * **Two spellings of one concept can land on one address** — `placement: 3` kept as written beside
 * `plcmt: 2` read as `accompanying`. Two leaves at one path is two answers in one body, so one wins and
 * the sample's disagreement is said. The member wins: it is the value the driver renders for the
 * source's version, where the raw number goes out under a fixed name.
 */
function oneLeafPerAddress(leaves: MappingLeaf[]): { leaves: MappingLeaf[]; conflicts: string[] } {
  const byAddress = new Map<string, MappingLeaf>();
  const conflicts: string[] = [];
  for (const leaf of leaves) {
    const address = pathToLabel(leaf.path);
    const held = byAddress.get(address);
    if (!held) {
      byAddress.set(address, leaf);
      continue;
    }
    if (JSON.stringify(held.staticValue) === JSON.stringify(leaf.staticValue) && held.kind === leaf.kind) continue;
    const winner = typeof leaf.staticValue === 'string' && typeof held.staticValue !== 'string' ? leaf : held;
    byAddress.set(address, winner);
    conflicts.push(describeConflictingConceptValues(mergeBasePathOf(leaf), JSON.stringify(winner.staticValue)));
  }
  return { leaves: [...byAddress.values()], conflicts };
}

function collapseIdenticalImpressions(body: Record<string, unknown>): {
  body: Record<string, unknown>;
  note: string | null;
} {
  const imp = body.imp;
  if (!Array.isArray(imp) || imp.length < 2) return { body, note: null };
  const first = canonicalJson(imp[0]);
  if (!imp.every((element) => canonicalJson(element) === first)) return { body, note: null };
  return {
    body: { ...body, imp: [imp[0]] },
    note:
      `The sample carried ${imp.length} identical impressions, so they were imported once, for every impression ` +
      `(imp[*]) — however many the pod asks for, not only the first ${imp.length}.`,
  };
}

/** JSON with object keys sorted, so two copies a partner serialised in a different key order compare equal. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * **A detected body leaf with its guess taken back** (`#1001`) — the parameter row's rule
 * (`revertDetectedRow`) for a leaf: what the sample carried, as a literal of its own type, keeping the
 * leaf's claims about its destination. A leaf with nothing to revert to is returned unchanged.
 */
export function revertDetectedLeaf(leaf: MappingLeaf): MappingLeaf {
  const value = revertValueOf(leaf.provenance);
  if (value === undefined) return leaf;
  return {
    path: leaf.path,
    kind: 'static',
    staticValue: value,
    ...(leaf.importance ? { importance: leaf.importance } : {}),
    ...(leaf.suppress ? { suppress: leaf.suppress } : {}),
    ...(leaf.acceptSystemDefault === false ? { acceptSystemDefault: false as const } : {}),
    provenance: originOf('detected'),
  };
}

/** A pre-mapped leaf for a catalog field: a `{macro}` where one fits, else empty. */
export function defaultLeafFor(
  section: string,
  relPath: string,
  leaves: MappingLeaf[] = [],
  /** Which impressions an `imp` field is for — every one unless chosen; see {@link buildFieldPath}. */
  scope: PatternScope = { kind: 'each' },
): MappingLeaf {
  const path = buildFieldPath(section, relPath, leaves, scope);
  const leafSegment = relPath.split('.').pop() ?? relPath;
  // **The protocol's claim is a seed, not a second opinion.** A field the spec requires starts
  // marked required, so the badge says one thing from the moment the row exists. Written only when
  // required — absent means nobody stated an importance, which is not the same as *optional*.
  const required = catalogRequiresPath(section, relPath) ? ({ importance: 'required' } as const) : {};

  // **A concept field is seeded before anything is suggested for it.** Its value comes from a
  // vocabulary we own, so a macro match on its *name* would be a coincidence, and the seed is
  // the member already in force on every request rather than an empty box beside it.
  const catalogField = getCatalogField(section, relPath);
  if (catalogField?.value?.from === 'concept') {
    return { path, ...required, kind: 'static', staticValue: seedValueFor(catalogField) ?? '' };
  }

  // **A slot only the operator can fill starts empty and says it is required** (`#1186`). No macro
  // could resolve it and no default could invent it — that is what `from: 'operator'` means — so
  // suggesting one would be the plausible-wrong-value case `endpointTemplate`'s rule is about. The
  // row carries the claim instead, which is what the required mark was built for: *You supply
  // this* on a template, unfinished on an instance.
  if (catalogField && needsOperatorValue(catalogField)) {
    return { path, importance: 'required', kind: 'static', staticValue: '' };
  }

  const suggestion = suggestMapping(leafSegment, undefined, 'body');
  if (suggestion.kind === 'macro' && suggestion.macroToken) {
    return { path, ...required, kind: 'expression', expression: macroExpression(suggestion.macroToken) };
  }
  const type = getCatalogField(section, relPath)?.valueType;
  return { path, ...required, kind: 'static', staticValue: type ? EMPTY_BY_TYPE[type] : '' };
}

/**
 * Baseline body for a mode: the required + recommended fields the operator has to supply,
 * pre-mapped. A starting point they refine, not a finished body.
 *
 * ## The two modes ask different questions, and only one of them is a list we own
 *
 * **Replace** owns the whole request — the driver builds nothing — so *what does a complete
 * OpenRTB request need* is the question, and `ORTB_REPLACE_STARTER_SECTIONS` is the right shape
 * for it: a vocabulary **we** decide, per the corollary's test.
 *
 * **Merge** asks *what will the driver not build*, which is a claim about the driver's code.
 * That was `ORTB_MERGE_STARTER_SECTIONS = ['regs', 'source']` — a hand-kept **section** list, and
 * this docblock said so itself: *"what is being expressed is whether a field is supplied
 * procedurally, which is a property of the field rather than of the section it sits in … until
 * then, a new procedural block in either driver has to be reflected here by hand, and nothing
 * fails if it is not."*
 *
 * **`#1117` built the declaration it was waiting for** — `ORTB_MERGE_BASE`, per field, with a
 * sentence for the operator, asserted against real requests by `libs/drivers/ortb-merge-base.spec.ts`
 * — and wired it into **validation** (`driverMergeBaseCovers`) and not into here. That is *a rule
 * attached to one path lapses when another is added*, with the two paths one function apart.
 *
 * ## What it cost, measured
 *
 * Exactly one field: **`imp.video.placement`**. The driver builds no placement, so validation
 * warns *"recommended but isn't set — the driver won't add it"* — while the starter, which exists
 * to prevent that warning, never seeds it because `imp` is not one of the two sections.
 * **The starter produced a body its own validator immediately complained about.**
 *
 * Nothing was over-seeded, which is why it stayed invisible: the list was never *wrong*, only
 * **short** — an inclusion test satisfied by a shorter list, arriving in a constant instead of a
 * gate.
 *
 * **Corrected 2026-09-15:** *"the driver builds no placement"* was true when measured and stopped
 * being true at `#1112`, which renders the placement concept on every request. The starter kept
 * seeding it because the declaration names the concept at `plcmt` and the lookup was literal; it now
 * answers under every spelling (`mergeBaseFieldAt`), so placement is neither seeded nor warned about.
 */
export function buildStarterLeaves(
  mode: 'merge' | 'replace',
  version: string | null,
  existing: MappingLeaf[] = [],
): MappingLeaf[] {
  const sections = mode === 'replace' ? ORTB_REPLACE_STARTER_SECTIONS : Object.keys(ORTB_FIELD_CATALOG);
  const seen = new Set(existing.map((l) => pathToLabel(l.path)));
  const added: MappingLeaf[] = [];
  for (const section of sections) {
    // Merge mode seeds only what the driver will not supply, and **for this source's version**
    // (`#1318` item 2): read without one, a 2.5 source was seeded 2.6's half of every
    // version-bounded pair. Both answers come from `getMissingStandardFields`, which *Add
    // recommended* and validation's fixes also ask, so the starter cannot seed a field the editor
    // then offers again or warns about.
    for (const f of getMissingStandardFields(section, new Set(), version, mode)) {
      const leaf = defaultLeafFor(section, f.name, existing);
      const label = pathToLabel(leaf.path);
      if (seen.has(label)) continue;
      seen.add(label);
      // `template` (R4/R4b): the catalog composed this leaf's value for the
      // protocol, not for this source. Unchipped would claim the operator authored
      // a starter body they never touched.
      added.push({ ...leaf, provenance: originOf('template') });
    }
  }
  return added;
}

/** Starter body as a storable template — for callers without an editor mounted. */
export function buildStarterTemplate(mode: 'merge' | 'replace', version: string | null): OrtbBodyTemplate {
  return leavesToTemplate(buildStarterLeaves(mode, version), mode);
}

/**
 * Apply what an `OrtbBodyIssue.fix` names — the one implementation of the body editor's **Fix** (R9,
 * #899): add each field, or, for a ref with `movedFrom`, **move** what is authored there.
 *
 * Two callers: the editor's `ValidationSummary`, which holds leaves, and the source
 * readiness panel, which stages the fix into the section's draft as a template
 * before the editor mounts ({@link applyOrtbBodyFixes}). Same reason as
 * `applyParamFix` — one Fix, one meaning.
 *
 * An **add** skips a field already present, so a stale panel fix cannot duplicate a leaf, and carries
 * `template` provenance for the reason `buildStarterLeaves` does: the catalog composed the value for
 * the protocol, not the operator for this source (R4b).
 *
 * A **move** re-roots every leaf at or beneath `movedFrom` — `source.ext.schain` is an object, so it
 * may be many leaves — and changes nothing else: the value, its mapping and its provenance are the
 * operator's, and only where it is sent changes. It does nothing if anything is already authored at
 * the destination, for the same stale-fix reason, and because then which value is meant is a choice.
 */
export function leavesWithFixes(leaves: MappingLeaf[], fixes: readonly OrtbFieldRef[]): MappingLeaf[] {
  let current = leaves;
  const seen = new Set(leaves.map((l) => pathToLabel(l.path)));
  const added: MappingLeaf[] = [];
  for (const { section, relPath, movedFrom } of fixes) {
    if (movedFrom !== undefined) {
      current = leavesMoved(
        current,
        buildFieldPath(section, movedFrom, current),
        buildFieldPath(section, relPath, current),
      );
      continue;
    }
    const leaf = defaultLeafFor(section, relPath);
    const label = pathToLabel(leaf.path);
    if (seen.has(label)) continue;
    seen.add(label);
    added.push({ ...leaf, provenance: originOf('template') });
  }
  return added.length === 0 ? current : [...current, ...added];
}

/** Whether `path` is `prefix` or lies beneath it. */
function isAtOrUnder(path: readonly PathSegment[], prefix: readonly PathSegment[]): boolean {
  return prefix.length <= path.length && prefix.every((segment, i) => path[i] === segment);
}

/** Every leaf at or under `from`, re-rooted at `to` — or `leaves` unchanged if `to` is occupied. */
function leavesMoved(leaves: MappingLeaf[], from: PathSegment[], to: PathSegment[]): MappingLeaf[] {
  if (!leaves.some((leaf) => isAtOrUnder(leaf.path, from))) return leaves;
  if (leaves.some((leaf) => isAtOrUnder(leaf.path, to) || isAtOrUnder(to, leaf.path))) return leaves;
  return leaves.map((leaf) =>
    isAtOrUnder(leaf.path, from) ? { ...leaf, path: [...to, ...leaf.path.slice(from.length)] } : leaf,
  );
}

/**
 * **Rename a body field** (Todd, 2026-09-28: *"let's add the ability to rename a field in the editor on
 * every mapping editor"*). Every other mapping editor already takes a typed name; this is the one whose
 * rows are keyed by their path, so a rename here is a **move of one leaf** — the same
 * {@link leavesMoved} a version-location Fix makes, so the two cannot disagree about what moving a
 * field keeps (everything but where it is sent).
 *
 * `nextName` is the part of the path after the section and any impression scope, dotted, the way
 * *Add field* takes it (`ext.gdpr`, `video.w`). The section and the scope stay as they are: moving a
 * field to another section or impression is a different act, and the tree groups rows by section.
 */
export type LeafRename = { ok: true; leaves: MappingLeaf[]; label: string } | { ok: false; problem: string };

/** The editable part of a leaf's path — see {@link renameLeaf}. */
export function leafName(leaf: MappingLeaf): string {
  return catalogSegments(pathWithinSection(leaf)).join('.');
}

export function renameLeaf(leaves: MappingLeaf[], leaf: MappingLeaf, nextName: string): LeafRename {
  const name = nextName.trim();
  const current = catalogSegments(pathWithinSection(leaf));
  if (name === current.join('.')) return { ok: true, leaves, label: pathToLabel(leaf.path) };
  if (!name) return { ok: false, problem: 'Enter a field name.' };
  const parts = name.split('.');
  if (parts.some((part) => part === '' || /\s|\[|\]/.test(part))) {
    return { ok: false, problem: 'A field name is keys separated by dots, with no spaces, brackets or empty parts.' };
  }
  const prefix = leaf.path.slice(0, leaf.path.length - current.length);
  const to: PathSegment[] = [...prefix, ...parts.map((part) => (/^\d+$/.test(part) ? Number(part) : part))];
  const moved = leavesMoved(leaves, leaf.path, to);
  if (moved === leaves) {
    return { ok: false, problem: `\`${pathToLabel(to)}\` is already in this body, or holds a field.` };
  }
  return { ok: true, leaves: moved, label: pathToLabel(to) };
}

/** {@link leavesWithFixes} against a stored template, preserving its mode. */
export function applyOrtbBodyFixes(
  template: OrtbBodyTemplate | undefined | null,
  fixes: readonly OrtbFieldRef[],
): OrtbBodyTemplate {
  return leavesToTemplate(leavesWithFixes(templateToLeaves(template), fixes), template?.mode ?? 'merge');
}

/**
 * Explode static **object** values that carry keys into individual sub-leaves —
 * the same list raw JSON would have produced. Typing `{"foo":"bar"}` into a
 * structured field's static box therefore yields the same guided rows as typing
 * it in the Raw view, each independently mappable to a macro or expression.
 *
 * Arrays are left alone: the guided view edits scalar arrays inline as a CSV
 * list, and exploding those into indexed leaves would take that away.
 */
export function expandStructuredLeaves(leaves: MappingLeaf[]): MappingLeaf[] {
  const out: MappingLeaf[] = [];
  for (const leaf of leaves) {
    const value = leaf.staticValue;
    const isObject = leaf.kind === 'static' && !!value && typeof value === 'object' && !Array.isArray(value);
    if (isObject && Object.keys(value as Record<string, unknown>).length > 0) {
      for (const sub of bodyToLeaves(value as Record<string, unknown>)) {
        out.push({ ...sub, path: [...leaf.path, ...sub.path] });
      }
      continue;
    }
    out.push(leaf);
  }
  return out;
}
